import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, FreeMode, Autoplay } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import restro from "../../../assets/orderingSystem/restrologo.png";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
  addItem,
  increaseQty,
  decreaseQty,
  clearCart as clearPublicCart,
} from "../../../slices/cart/publicOrderingCartSlice";
import Header from "../../PublicOrdering/pages/Header";
import OrderTracking from "../../PublicOrdering/pages/OrderTracking";
import OrderDetailsModal from "../../PublicOrdering/pages/OrderDetailsModal";
import CartSummary from "../../PublicOrdering/pages/CartSummary";
import toast from "react-hot-toast";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";
import playBeep from "../../PublicOrdering/pages/PlayBeep";
import ThemeFooter from "../component/ThemeFooter";
import TouchBasedHeader from "../../PublicOrdering/TourchBased/TouchBasedHeader";
import PublicOrderingLayout from "./PublicOrderingLayout";

// New component imports
import {
  VideoAdOverlay,
  CartIdleWarningModal,
  ProductOptionsModal,
  TouchBasedLayout,
  NormalLayout,
} from "./components";

// Custom hooks imports
import { useIdleCartDeletion, useVideoAdManagement } from "./hooks";

// Services imports
import ItemService from "../../../services/OrderingServices/ItemService";
import OrganizationService from "../../../services/OrderingServices/OrganizationService";

// ============= SEARCH BAR WITH CART BUTTON COMPONENT =============
const SearchBarWithCart = ({
  menuItems,
  onSearch,
  totalItems,
  onCartClick,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 992);

  const handleChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    onSearch(term);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className=" ">
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        {/* Search Input */}
        <div style={{ position: "relative", flex: 1 }}>
          <input
            type="text"
            placeholder="Search menu items..."
            value={searchTerm}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: "12px 16px 12px 40px",
              borderRadius: "30px",
              border: "2px solid #e0e0e0",
              fontSize: "14px",
              backgroundColor: "#f9f9f9",
              transition: "all 0.3s ease",
              height: "36px",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#ff7900";
              e.target.style.boxShadow = "0 0 0 3px rgba(255, 121, 0, 0.15)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e0e0e0";
              e.target.style.boxShadow = "none";
            }}
          />
          <i
            className="bx bx-search"
            style={{
              position: "absolute",
              left: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "18px",
              color: "#999",
            }}
          />
        </div>

        {/* Cart Button */}
        {isSmallScreen && (
          <Button
            color="link"
            onClick={() => {
              playBeep();
              onCartClick();
            }}
            disabled={totalItems === 0}
            style={{
              position: "relative",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "#ff7900",
              border: "none",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                totalItems > 0 ? "0 2px 8px rgba(255, 121, 0, 0.3)" : "none",
              transition: "all 0.3s ease",
              flexShrink: 0,
              opacity: totalItems > 0 ? 1 : 0.5,
              cursor: totalItems > 0 ? "pointer" : "not-allowed",
            }}
            onMouseEnter={(e) => {
              if (totalItems > 0) {
                e.currentTarget.style.backgroundColor = "#e56a00";
                e.currentTarget.style.transform = "scale(1.05)";
              }
            }}
            onMouseLeave={(e) => {
              if (totalItems > 0) {
                e.currentTarget.style.backgroundColor = "#ff7900";
                e.currentTarget.style.transform = "scale(1)";
              }
            }}
          >
            <i
              className="bx bx-cart"
              style={{ fontSize: "22px", color: "#fff" }}
            />
            {totalItems > 0 && (
              <Badge
                color="danger"
                pill
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  fontSize: "9px",
                  padding: "3px 6px",
                  backgroundColor: "#dc3545",
                  fontWeight: "700",
                  minWidth: "18px",
                }}
              >
                {totalItems}
              </Badge>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

// ============= CATEGORIES SWIPER COMPONENT =============
const CategoriesSwiper = ({ categories, activeTab, setActiveTab }) => (
  <div>
    <Swiper
      modules={[FreeMode]}
      spaceBetween={12}
      slidesPerView="auto"
      freeMode={{
        enabled: true,
        momentum: true,
        momentumBounce: false,
        momentumRatio: 0.9,
      }}
      grabCursor={true}
    >
      {categories.map((cat) => (
        <SwiperSlide key={cat.id} style={{ width: "auto" }}>
          <Button
            size="sm"
            className="text-nowrap"
            style={{
              borderRadius: "20px",
              padding: "6px 18px",
              fontWeight: activeTab === cat.id ? "600" : "500",
              fontSize: "12px",
              border: activeTab === cat.id ? "none" : "1px solid #e0e0e0",
              backgroundColor: activeTab === cat.id ? "#ff7900" : "#fff",
              color: activeTab === cat.id ? "#fff" : "#495057",
              boxShadow:
                activeTab === cat.id
                  ? "0 2px 6px rgba(255, 121, 0, 0.25)"
                  : "none",
            }}
            onClick={() => {
              playBeep();
              setActiveTab(cat.id);
            }}
          >
            {cat.name}
          </Button>
        </SwiperSlide>
      ))}
    </Swiper>
  </div>
);

// ============= MAIN COMPONENT =============
export const ThemeMenu2 = ({
  orgInfo,
  orgData,
  ads = [],
  isVideoPlaying,
  setIsVideoPlaying,
}) => {
  console.log("🚀 ~ ThemeMenu2 ~ ads:", ads);
  console.log("🚀 ~ ThemeMenu2 ~ orgInfo:", orgInfo);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use publicOrderingCart slice
  const publicCartState = useSelector(
    (s) =>
      s.publicOrderingCart || {
        items: [],
        totalQty: 0,
        totalAmount: 0,
      },
  );

  const orgCode = orgData?.orgCode;
  // const orgCode = "DAN5434"; // For testing multiple sub-tenants
  const cart = publicCartState.items || [];
  const appType = window?.APP_CONFIG?.appType;
  const isTouchBased = appType === "TouchBased";

  // ============= STATE =============
  const [activeTab, setActiveTab] = useState("all");
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showScroll, setShowScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [productNotes, setProductNotes] = useState("");
  const [productQty, setProductQty] = useState(1);
  const [cartOffcanvasOpen, setCartOffcanvasOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCartWarningModal, setShowCartWarningModal] = useState(false);
  const [cartDeleteCountdown, setCartDeleteCountdown] = useState(10);
  const [showVideoAd, setShowVideoAd] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoPlayKey, setVideoPlayKey] = useState(0); // 🔄 Force video replay
  const [deviceInfo, setDeviceInfo] = useState(null);

  const [multipleTenant, setMultipleTenant] = useState(false);
  const [fetchTenantTimer, setFetchTenantTimer] = useState(600000); // 10 minutes
  console.log("🚀 ~ ThemeMenu2 ~ multipleTenant:", multipleTenant);

  const [isTouchBasedSetting, setIsTouchBasedSetting] = useState(() => {
    const stored = localStorage.getItem("isTouchBasedSetting");
    return stored !== null ? JSON.parse(stored) : true;
  });

  //Boolean for the multiple tenant
  // const multipleTenant = true;

  console.log("🔍 Theme2 Component State:", {
    multipleTenant,
    orgCode,
    orgData: orgData?.orgCode,
    macIdInStorage: localStorage.getItem("macId"),
  });

  // ============= MULTI SUB-TENANT STATE =============
  // Will be populated from API based on device macId
  const [multiSubOrgCodeTenant, setMultiSubOrgCodeTenant] = useState(() => {
    // Initialize with current orgCode if available
    return orgCode ? [orgCode] : [];
  });

  // Start with current orgCode
  const [selectedSubTenant, setSelectedSubTenant] = useState(orgCode);
  console.log("🚀 ~ ThemeMenu2 ~ selectedSubTenant:", selectedSubTenant);
  const [subTenantLoading, setSubTenantLoading] = useState(false);
  // Initialize with existing data from props
  const [subTenantData, setSubTenantData] = useState(orgInfo);
  const [subTenantOrgData, setSubTenantOrgData] = useState(orgData);
  const [subTenantError, setSubTenantError] = useState(null);
  const [fetchTrigger, setFetchTrigger] = useState(0); // Used to force refetch
  // Store org names for display: { orgCode: "Restaurant Name" }
  const [orgNameMapping, setOrgNameMapping] = useState(() => {
    // Initialize with current org if available
    if (orgData?.orgCode && orgData?.organization?.name) {
      return { [orgData.orgCode]: orgData.organization.name };
    }
    return {};
  });

  // ============= CUSTOM HOOKS =============
  const {
    videoAds,
    imageAds,
    videoShownAtRef,
    hasLoggedCurrentPlaybackRef,
    logVideoAdImpression,
  } = useVideoAdManagement({ ads, deviceInfo });

  const { warningTimeoutRef } = useIdleCartDeletion({
    isTouchBased,
    cartLength: cart.length,
    videoAds,
    onShowCartWarning: () => setShowCartWarningModal(true),
    onHideCartWarning: () => {
      setShowCartWarningModal(false);
      setCartDeleteCountdown(10);
    },
    onShowVideoAd: () => setShowVideoAd(true),
  });

  // ============= EFFECTS =============
  useEffect(() => {
    localStorage.setItem(
      "isTouchBasedSetting",
      JSON.stringify(isTouchBasedSetting),
    );
  }, [isTouchBasedSetting]);

  useEffect(() => {
    if (orgInfo && orgData) {
      const timer = setTimeout(() => setLoading(false), 800);
      return () => clearTimeout(timer);
    }
  }, [orgInfo, orgData]);

  useEffect(() => {
    try {
      const deviceContext = localStorage.getItem("deviceContext");
      const macId = localStorage.getItem("macId");
      const ipAddress = localStorage.getItem("ipAddress") || "0.0.0.0";

      if (deviceContext) {
        const context = JSON.parse(deviceContext);
        setDeviceInfo({
          macId: macId,
          deviceName: context.deviceName || "Unknown Device",
          ipAddress: ipAddress,
        });
      }
    } catch (error) {
      console.error("Error reading device info:", error);
    }
  }, []);

  // ============= FETCH DEVICE ORGANIZATIONS =============
  // useEffect(() => {
  //   const fetchDeviceOrganizations = async () => {
  //     try {
  //       const macId = localStorage.getItem("macId");

  //       if (!macId) {
  //         console.warn("⚠️ No macId found in localStorage");
  //         return;
  //       }

  //       console.log("🔄 Fetching device organizations for macId:", macId);

  //       const organizations = await OrganizationService.getDeviceOrganizations(
  //         macId,
  //       );

  //       if (!Array.isArray(organizations)) {
  //         console.warn("⚠️ Invalid response");
  //         setMultipleTenant(false);
  //         return;
  //       }

  //       // 🔥 Determine multiple tenant
  //       setMultipleTenant(organizations.length > 1);

  //       console.log("✅ Device organizations fetched:", organizations);

  //       const orgCodes = organizations.map((org) => org.orgCode);
  //       const nameMapping = {};

  //       organizations.forEach((org) => {
  //         if (org.orgCode && org.name) {
  //           nameMapping[org.orgCode] = org.name;
  //         }
  //       });

  //       const currentOrgCode = orgData?.orgCode;

  //       const uniqueOrgCodes = [
  //         ...new Set([currentOrgCode, ...orgCodes].filter(Boolean)),
  //       ];

  //       setMultiSubOrgCodeTenant(uniqueOrgCodes);
  //       setOrgNameMapping((prev) => ({ ...prev, ...nameMapping }));

  //       console.log("✅ Organization list updated:", uniqueOrgCodes);

  //       toast.success(`Found ${organizations.length} restaurants`, {
  //         duration: 2000,
  //       });
  //     } catch (error) {
  //       console.error("❌ Error fetching device organizations:", error);
  //       setMultipleTenant(false);
  //     }
  //   };

  //   fetchDeviceOrganizations();
  // }, []);

  useEffect(() => {
    let interval;

    const fetchDeviceOrganizations = async () => {
      try {
        const macId = localStorage.getItem("macId");
        if (!macId) return;

        console.log("🔄 Fetching device organizations...");

        const organizations = await OrganizationService.getDeviceOrganizations(
          macId,
        );

        if (!Array.isArray(organizations)) {
          setMultipleTenant(false);
          return;
        }

        const newMultipleTenant = organizations.length >= 1;

        const orgCodes = organizations.map((org) => org.orgCode);
        const nameMapping = {};

        organizations.forEach((org) => {
          if (org.orgCode && org.name) {
            nameMapping[org.orgCode] = org.name;
          }
        });

        const currentOrgCode = orgData?.orgCode;

        const uniqueOrgCodes = [
          ...new Set([currentOrgCode, ...orgCodes].filter(Boolean)),
        ];

        // Only update if data has changed to prevent unnecessary re-renders
        setMultipleTenant((prev) => {
          if (prev !== newMultipleTenant) {
            console.log(
              "✅ Multiple tenant status updated:",
              newMultipleTenant,
            );
            return newMultipleTenant;
          }
          return prev;
        });

        setMultiSubOrgCodeTenant((prev) => {
          const hasChanged =
            prev.length !== uniqueOrgCodes.length ||
            !prev.every((code, idx) => code === uniqueOrgCodes[idx]);

          if (hasChanged) {
            console.log("✅ Restaurants updated:", uniqueOrgCodes);
            return uniqueOrgCodes;
          }
          return prev;
        });

        setOrgNameMapping((prev) => {
          const hasChanged = Object.keys(nameMapping).some(
            (key) => prev[key] !== nameMapping[key],
          );

          if (hasChanged) {
            console.log("✅ Organization names updated");
            return { ...prev, ...nameMapping };
          }
          return prev;
        });
      } catch (error) {
        console.error("❌ Error fetching organizations:", error);
      }
    };

    // first call
    fetchDeviceOrganizations();

    // 🔁 auto refresh every 10 minutes
    interval = setInterval(fetchDeviceOrganizations, fetchTenantTimer);

    return () => clearInterval(interval);
  }, [orgData, fetchTenantTimer]);

  // ============= FETCH SUB-TENANT DATA =============
  useEffect(() => {
    const fetchSubTenantData = async () => {
      if (!selectedSubTenant || !multipleTenant) return;

      // If selectedSubTenant is the same as current orgCode and we have data, use it
      // DISABLED: Always fetch to ensure fresh data
      // if (selectedSubTenant === orgCode && orgInfo && orgData) {
      //   console.log("✅ Using current org data, no need to fetch");
      //   setSubTenantData(orgInfo);
      //   setSubTenantOrgData(orgData);
      //   setSubTenantError(null);
      //   return;
      // }

      setSubTenantLoading(true);
      setSubTenantError(null);

      try {
        console.log("🔄 Fetching data for sub-tenant:", selectedSubTenant);
        console.log("🔄 Current orgCode for comparison:", orgCode);
        console.log("🔄 multipleTenant flag:", multipleTenant);
        console.log(
          "🔄 API Endpoint: ItemService.itemTheme() with orgCode:",
          selectedSubTenant,
        );

        // Fetch item theme data
        const itemResponse = await ItemService.itemTheme(selectedSubTenant);
        console.log("✅ Sub-tenant item data:", itemResponse);

        if (!itemResponse || typeof itemResponse !== "object") {
          throw new Error(
            `Invalid item data received: ${JSON.stringify(itemResponse)}`,
          );
        }

        setSubTenantData(itemResponse);

        // Fetch organization details
        console.log("🔄 Fetching organization details for:", selectedSubTenant);
        const orgResponse = await OrganizationService.detailByCode(
          selectedSubTenant,
        );
        console.log("✅ Sub-tenant org data:", orgResponse);

        if (!orgResponse) {
          throw new Error("No organization data received from API");
        }

        setSubTenantOrgData(orgResponse);
        setSubTenantError(null);

        // Extract organization name from response (handle both flat and nested structures)
        const orgName =
          orgResponse?.name ||
          orgResponse?.organization?.name ||
          selectedSubTenant;

        // Update org name mapping
        setOrgNameMapping((prev) => ({
          ...prev,
          [selectedSubTenant]: orgName,
        }));

        toast.success(`Switched to ${orgName}`, { duration: 2000 });
      } catch (error) {
        console.error("❌ Error fetching sub-tenant data:", error);
        console.error("❌ Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
        });

        const errorMsg =
          error.response?.data?.message ||
          error.response?.statusText ||
          error.message ||
          "Failed to load sub-tenant data";

        setSubTenantError({
          message: errorMsg,
          orgCode: selectedSubTenant,
          details: error.response?.data || error.message,
        });

        toast.error(`${selectedSubTenant}: ${errorMsg}`, { duration: 4000 });

        // Revert to previous selection if available
        if (subTenantData && subTenantOrgData) {
          toast("Keeping previous data", { duration: 2000 });
        }
      } finally {
        setSubTenantLoading(false);
      }
    };

    fetchSubTenantData();
  }, [selectedSubTenant, multipleTenant, fetchTrigger]);

  useEffect(() => {
    if (!videoAds.length) {
      setCurrentVideoIndex(0);
      return;
    }
    if (currentVideoIndex >= videoAds.length) {
      setCurrentVideoIndex(0);
    }
  }, [videoAds, currentVideoIndex]);

  useEffect(() => {
    // Use sub-tenant data if multipleTenant is enabled and data is available
    const dataSource =
      multipleTenant && subTenantData ? subTenantData : orgInfo;

    if (dataSource && (multipleTenant ? subTenantOrgData : orgData)) {
      const data = dataSource;
      const catKeys = Object.keys(data);
      const formattedCategories = catKeys.map((key) => ({
        id: key,
        name: key,
      }));
      setCategories([{ id: "all", name: "All Items" }, ...formattedCategories]);

      const items = [];
      catKeys.forEach((key) => {
        data[key].forEach((item) => {
          items.push({ ...item, category: key });
        });
      });
      setMenuItems(items);
    }
  }, [orgInfo, orgData, subTenantData, subTenantOrgData, multipleTenant]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ============= HANDLERS =============
  const addToCart = (item) => {
    setSelectedAddons([]);
    setProductNotes("");
    setProductQty(1);
    setSelectedProduct(item);
    setTimeout(() => {
      setProductModalOpen(true);
    }, 0);
  };

  const addImmediateToCart = (item) => {
    const entry = cart.find(
      (c) =>
        c.productId === item.id &&
        (!c.addons || c.addons.length === 0) &&
        (!c.attributes || c.attributes.length === 0),
    );
    if (entry) {
      dispatch(increaseQty(entry.uid));
    } else {
      dispatch(
        addItem({
          id: item.id,
          name: item.name,
          basePrice: item.price || item.basePrice || 0,
          attributes: item.attributes || {},
          addons: [],
          extras: [],
        }),
      );
    }
    playBeep();
    toast.success(`Added ${item.name} to cart`);
  };

  const handleToggleAddon = (addonIndex) => {
    setSelectedAddons((prev) => {
      if (prev.includes(addonIndex))
        return prev.filter((i) => i !== addonIndex);
      return [...prev, addonIndex];
    });
  };

  const handleConfirmAddToCart = () => {
    if (!selectedProduct) return;

    const addons = (selectedProduct.addons || []).filter((_, idx) =>
      selectedAddons.includes(idx),
    );

    const payload = {
      id: selectedProduct.id,
      name: selectedProduct.name,
      basePrice: selectedProduct.price || selectedProduct.basePrice || 0,
      attributes: selectedProduct.attributes || {},
      addons: addons,
      extras: selectedProduct.extras || [],
      note: productNotes,
    };

    playBeep();
    for (let i = 0; i < (productQty || 1); i++) {
      dispatch(addItem(payload));
    }
    toast.success("Order added to cart");
    setProductModalOpen(false);
    setSelectedProduct(null);
    setSelectedAddons([]);
    setProductNotes("");
    setProductQty(1);
  };

  // 🔄 Fetch fresh videos and continue playback
  const fetchFreshVideosAndContinue = async () => {
    try {
      if (!deviceInfo?.macId) {
        console.log("⏭️ No device info, repeating current video");
        setVideoPlayKey((prev) => prev + 1); // Force replay
        return; // Repeat current video
      }

      console.log("🔄 Fetching fresh videos from server...");
      const AdLogService = await import(
        "../../../../services/AdService/AdLogService"
      )
        .then((m) => m.default || m)
        .catch(() => null);

      if (!AdLogService?.getAdsByMacAddress) {
        console.log("⏭️ AdLogService unavailable, repeating current video");
        setVideoPlayKey((prev) => prev + 1); // Force replay
        return; // Repeat current video
      }

      const freshAds = await AdLogService.getAdsByMacAddress(deviceInfo.macId);
      const baseApi =
        window.APP_CONFIG?.api4?.baseapi ||
        "https://api-databasewebservice.danfesolution.com";

      const resolveMediaUrl = (path) => {
        if (!path) return "";
        if (/^https?:\/\//i.test(path)) return path;
        if (path.startsWith("/")) return `${baseApi}${path}`;
        return `${baseApi}/${path}`;
      };

      const freshVideoAds = (Array.isArray(freshAds) ? freshAds : [])
        .filter((ad) => Number(ad.mediaType) === 2)
        .map((ad) => ({
          id: ad.id,
          title: ad.title,
          description: ad.description,
          mediaType: Number(ad.mediaType),
          mediaPath: resolveMediaUrl(ad.mediaPath),
          priority: ad.priority ?? 999,
          durationSeconds: ad.durationSeconds || 10,
        }));

      console.log("📹 Fresh videos fetched:", freshVideoAds.length);

      if (freshVideoAds.length > 0) {
        // Check if new videos are available
        const currentVideoId = videoAds[currentVideoIndex]?.id;
        const newVideoExists = freshVideoAds.some(
          (v) => v.id !== currentVideoId,
        );

        if (newVideoExists) {
          console.log("✅ New video found, moving to next");
          setCurrentVideoIndex(
            (prevIndex) => (prevIndex + 1) % videoAds.length,
          );
        } else {
          console.log("🔄 Same videos, repeating current video");
          // Repeat current video by forcing a key change
          setVideoPlayKey((prev) => prev + 1);
        }
      } else {
        console.log("⏭️ No fresh videos available, repeating current video");
        // Repeat current video
        setVideoPlayKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("❌ Error fetching fresh videos:", error);
      // On error, repeat current video silently
      setVideoPlayKey((prev) => prev + 1);
    }
  };

  const handleVideoEnded = () => {
    console.log("✅ Video ended:", videoAds[currentVideoIndex]?.title);
    if (hasLoggedCurrentPlaybackRef.current) {
      // Already logged, just fetch fresh and continue
      fetchFreshVideosAndContinue();
      return;
    }

    const currentAd = videoAds[currentVideoIndex];
    const hiddenAt = new Date();
    const shownAt = videoShownAtRef.current || hiddenAt;
    const playedSeconds = Math.max(
      0,
      Math.round((hiddenAt.getTime() - shownAt.getTime()) / 1000),
    );

    if (currentAd) {
      hasLoggedCurrentPlaybackRef.current = true;
      logVideoAdImpression({
        ad: currentAd,
        shownAt,
        hiddenAt,
        playedSeconds,
        wasCompleted: true,
        wasSkipped: false,
      });
    }

    videoShownAtRef.current = null;

    // 🎯 Fetch fresh videos and continue (don't close overlay, don't stop video mode)
    fetchFreshVideosAndContinue();

    // ❌ DO NOT call setShowVideoAd(false) - keep overlay open
    // ❌ DO NOT call setIsVideoPlaying(false) - keep only video logs, no image logs
  };

  const handleVideoSkipped = async () => {
    console.log("Dismiss triggered");
    if (hasLoggedCurrentPlaybackRef.current) {
      setShowVideoAd(false);
      setIsVideoPlaying(false);
      return;
    }

    const currentAd = videoAds[currentVideoIndex];
    const hiddenAt = new Date();
    const shownAt = videoShownAtRef.current || hiddenAt;
    const playedSeconds = Math.max(
      0,
      Math.round((hiddenAt.getTime() - shownAt.getTime()) / 1000),
    );

    if (currentAd) {
      hasLoggedCurrentPlaybackRef.current = true;
      logVideoAdImpression({
        ad: currentAd,
        shownAt,
        hiddenAt,
        playedSeconds,
        wasCompleted: false,
        wasSkipped: true,
      });
    }

    videoShownAtRef.current = null;
    setShowVideoAd(false);
    setIsVideoPlaying(false); // 🔥 Resume image ads
  };

  const handleVideoPlay = () => {
    console.log("▶Video started playing:", videoAds[currentVideoIndex]?.title);
    videoShownAtRef.current = new Date();
    hasLoggedCurrentPlaybackRef.current = false;
    setIsVideoPlaying(true); // 🔥 STOP image ads
  };

  const handleVideoError = () => {
    console.error(
      "❌ Video failed to load. Path:",
      videoAds[currentVideoIndex]?.mediaPath,
    );
    setTimeout(() => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videoAds.length);
    }, 1000);
  };

  // ============= COMPUTED VALUES =============
  // Use sub-tenant orgData when multipleTenant is enabled and sub-tenant data is available
  const activeOrgData = useMemo(() => {
    if (multipleTenant && subTenantOrgData) {
      console.log("📊 Using sub-tenant org data:", subTenantOrgData);
      return subTenantOrgData;
    }
    console.log("📊 Using original org data:", orgData);
    return orgData;
  }, [multipleTenant, subTenantOrgData, orgData]);

  const filteredItems = useMemo(() => {
    let items =
      activeTab === "all"
        ? menuItems
        : menuItems.filter((item) => item.category === activeTab);

    if (searchTerm.trim()) {
      items = items.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return items;
  }, [activeTab, menuItems, searchTerm]);

  const totalItems =
    publicCartState.totalQty || cart.reduce((s, i) => s + (i.qty || 0), 0);
  const totalPrice =
    publicCartState.totalAmount ||
    cart.reduce(
      (s, i) => s + (i.totalPrice || i.basePrice || 0) * (i.qty || 0),
      0,
    );

  const customer = JSON.parse(localStorage.getItem("CustomerInfo"));
  const isLoggedIn = customer?.accessToken;

  // ============= ORDER HANDLERS =============
  const handleOrderSubmit = (formData) => {
    const orderItems = (cart || []).map((ci) => {
      const product = menuItems.find((m) => m.id === ci.id) || {};
      const unitPrice = ci.basePrice || product.price || product.basePrice || 0;
      const qty = ci.qty || ci.quantity || 0;
      return {
        id: ci.id,
        name: ci.name || product.name || "",
        quantity: qty,
        price: unitPrice,
        addons: ci.addons || [],
        extras: ci.extras || [],
        attributes: ci.attributes || {},
        total: (ci.totalPrice || unitPrice) * qty,
      };
    });

    const computedTotal = orderItems.reduce((s, it) => s + (it.total || 0), 0);

    const order = {
      orderId: `ORD-${Date.now()}`,
      items: orderItems,
      total: computedTotal,
      totalItems: orderItems.reduce((s, it) => s + it.quantity, 0),
      customer: formData,
      status: "Received",
      placedTime: new Date().toLocaleString(),
      orgCode: activeOrgData?.orgCode || orgCode,
    };

    // TouchBased: No login required
    if (isTouchBased) {
      try {
        localStorage.setItem("pendingOrder", JSON.stringify(order));
      } catch (err) {
        console.error("Error saving pending order:", err);
      }
      dispatch(clearPublicCart());
      setModalOpen(false);
      navigate(`/order-summary`);
      return;
    }

    // Normal: Login required
    if (!isLoggedIn) {
      try {
        localStorage.setItem("pendingOrder", JSON.stringify(order));
      } catch (err) {
        console.error("Error saving pending order:", err);
      }
      dispatch(clearPublicCart());
      setModalOpen(false);
      navigate(`/${orgCode}/login`);
      return;
    }

    // If logged in, place order
    try {
      const stored = localStorage.getItem("placedOrders");
      const arr = stored ? JSON.parse(stored) : [];
      arr.push(order);
      localStorage.setItem("placedOrders", JSON.stringify(arr));
      localStorage.setItem("lastPlacedOrder", JSON.stringify(order));
    } catch (err) {
      console.error("Error placing order:", err);
    }

    dispatch(clearPublicCart());
    setModalOpen(false);
    navigate(`/${orgCode}/order-summary`, {
      state: { orgData: activeOrgData },
    });
  };

  const toggleCartOffcanvas = () => {
    if (window.innerWidth < 992) {
      setCartOffcanvasOpen((s) => !s);
    }
  };

  // ============= RENDER =============
  return (
    <>
      <PublicOrderingLayout
        orgData={activeOrgData}
        orgcode={orgCode}
        isTouchBasedSetting={isTouchBasedSetting}
        setIsTouchBasedSetting={setIsTouchBasedSetting}
        isVideoPlaying={isVideoPlaying}
      >
        {/* Search Bar & Categories */}
        <Row className="bg-light px-0 py-0 mb-3 align-items-center">
          {multipleTenant && (
            <Col md={12}>
              <div
                className="bg-light border rounded p-3 mb-3 mt-0"
                // style={{
                //   boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                // }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 fw-semibold text-dark">
                    <i className="bx bx-buildings me-2"></i>
                    More Restaurant
                  </h6>
                  {/* {subTenantLoading && (
                    <div className="text-primary">
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Loading...
                    </div>
                  )} */}
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {multiSubOrgCodeTenant.map((orgCodeItem, index) => {
                    const displayName =
                      orgNameMapping[orgCodeItem] || orgCodeItem;
                    const isSelected = selectedSubTenant === orgCodeItem;
                    const isLoading = subTenantLoading && isSelected;

                    return (
                      <Button
                        key={index}
                        onClick={() => {
                          playBeep();
                          setSelectedSubTenant(orgCodeItem);
                        }}
                        disabled={subTenantLoading}
                        style={{
                          borderRadius: "8px",
                          padding: "8px 16px",
                          fontSize: "13px",
                          fontWeight: "500",
                          border: isSelected
                            ? "2px solid #ff7900"
                            : "1px solid #e0e0e0",
                          backgroundColor: isSelected ? "#ff7900" : "#fff",
                          color: isSelected ? "#fff" : "#495057",
                          boxShadow: isSelected
                            ? "0 2px 6px rgba(255, 121, 0, 0.3)"
                            : "0 1px 3px rgba(0,0,0,0.1)",
                          transition: "all 0.2s ease",
                          minWidth: "120px",
                        }}
                      >
                        {isLoading ? (
                          <div className="d-flex align-items-center">
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></span>
                            Loading...
                          </div>
                        ) : (
                          <div className="d-flex align-items-center">
                            <i className="bx bx-store me-1"></i>
                            {displayName}
                          </div>
                        )}
                      </Button>
                    );
                  })}
                </div>

                {/* Success State */}
                {/* {subTenantOrgData && !subTenantError && !subTenantLoading && (
                  <div className="mt-3 p-2 bg-success-subtle rounded border border-success">
                    <small className="text-success d-flex align-items-center">
                      <i className="bx bx-check-circle me-1"></i>
                      <strong className="me-1">✅ Active:</strong>
                      {subTenantOrgData?.organization?.name ||
                        selectedSubTenant}
                    </small>
                  </div>
                )} */}

                {/* Error State */}
                {/* {subTenantError && (
                  <div className="mt-3 p-3 bg-danger-subtle rounded border border-danger">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <small className="text-danger d-flex align-items-center mb-1">
                          <i className="bx bx-error-circle me-1"></i>
                          <strong className="me-1">Error:</strong>
                          {subTenantError.message}
                        </small>
                        <small className="text-muted d-block">
                          OrgCode: <code>{subTenantError.orgCode}</code>
                        </small>
                        {subTenantError.details && (
                          <small className="text-muted d-block mt-1">
                            Details:{" "}
                            {JSON.stringify(subTenantError.details).substring(
                              0,
                              100,
                            )}
                            ...
                          </small>
                        )}
                      </div>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        onClick={() => {
                          playBeep();
                          setFetchTrigger((prev) => prev + 1); // Force refetch
                        }}
                        style={{ minWidth: "70px" }}
                      >
                        <i className="bx bx-refresh me-1"></i>
                        Retry
                      </Button>
                    </div>
                  </div>
                )} */}
              </div>
            </Col>
          )}

          <Col md={3}>
            {loading ? (
              <Skeleton height={36} borderRadius={30} />
            ) : (
              <SearchBarWithCart
                menuItems={menuItems}
                onSearch={setSearchTerm}
                totalItems={totalItems}
                onCartClick={toggleCartOffcanvas}
              />
            )}
          </Col>
          <Col md={9} className="align-content-center align-items-center">
            {loading ? (
              <div className="d-flex gap-2 px-2">
                {[...Array(10)].map((_, idx) => (
                  <Skeleton
                    key={idx}
                    width={80}
                    height={30}
                    borderRadius={20}
                  />
                ))}
              </div>
            ) : (
              <CategoriesSwiper
                categories={categories}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            )}
          </Col>
        </Row>

        {/* Conditional Layout Rendering */}
        {isTouchBasedSetting ? (
          <NormalLayout
            loading={loading}
            filteredItems={filteredItems}
            cart={cart}
            onAddItem={addToCart}
            onRemoveItem={() => {}}
            onCardClick={addImmediateToCart}
            onMyOrdersClick={() => setTrackingOpen(true)}
            orgData={activeOrgData}
            multipleTenant={multipleTenant}
          />
        ) : (
          <TouchBasedLayout
            loading={loading}
            filteredItems={filteredItems}
            cart={cart}
            imageAds={imageAds}
            isTouchBasedSetting={isTouchBasedSetting}
            onAddItem={addToCart}
            onRemoveItem={() => {}}
            onCardClick={addImmediateToCart}
            orgData={activeOrgData}
            multipleTenant={multipleTenant}
          />
        )}
      </PublicOrderingLayout>

      {/* Cart & Order Modals */}
      {cart.length > 0 && (
        <>
          <CartSummary
            cart={cart}
            onClear={() => dispatch(clearPublicCart())}
            onOrderNow={() => setModalOpen(true)}
            orgInfo={activeOrgData}
            orgData={activeOrgData}
            isOpen={cartOffcanvasOpen}
            toggle={toggleCartOffcanvas}
          />

          <OrderDetailsModal
            isOpen={modalOpen}
            toggle={() => setModalOpen(false)}
            onSubmit={handleOrderSubmit}
            totalPrice={totalPrice}
            totalItems={totalItems}
          />
        </>
      )}

      {/* Product Options Modal */}
      <ProductOptionsModal
        isOpen={productModalOpen && selectedProduct !== null}
        selectedProduct={selectedProduct}
        selectedAddons={selectedAddons}
        productNotes={productNotes}
        productQty={productQty}
        onToggleAddon={handleToggleAddon}
        onNotesChange={setProductNotes}
        onQtyChange={setProductQty}
        onAddToCart={handleConfirmAddToCart}
        onClose={() => setProductModalOpen(false)}
      />

      {/* Order Tracking */}
      <OrderTracking
        isOpen={trackingOpen}
        toggle={() => setTrackingOpen(false)}
        orgData={activeOrgData}
      />

      {/* Cart Idle Warning Modal (TouchBased only) */}
      {isTouchBased && (
        <CartIdleWarningModal
          isOpen={showCartWarningModal}
          onClose={() => {
            setShowCartWarningModal(false);
            if (warningTimeoutRef.current) {
              clearTimeout(warningTimeoutRef.current);
              warningTimeoutRef.current = null;
            }
          }}
          countdownSeconds={cartDeleteCountdown}
        />
      )}

      {/* Video Ad Overlay (TouchBased only) */}
      {isTouchBased && showVideoAd && videoAds.length > 0 && (
        <VideoAdOverlay
          videoAds={videoAds}
          currentVideoIndex={currentVideoIndex}
          videoPlayKey={videoPlayKey}
          onVideoEnded={handleVideoEnded}
          onVideoSkipped={handleVideoSkipped}
          onVideoPlay={handleVideoPlay}
          onVideoError={handleVideoError}
          videoShownAtRef={videoShownAtRef}
          hasLoggedCurrentPlaybackRef={hasLoggedCurrentPlaybackRef}
        />
      )}
    </>
  );
};

export default ThemeMenu2;
